import { onUnmounted } from 'vue';
import { SessionExpiredError, redirectToSessionExpired } from '@Utils/sessionExpired';

/** How often a status screen polls the auth-bff. */
export const POLL_INTERVAL_MS = 5_000;

/** Consecutive failed polls before giving up: 6 polls at 5s apart is 30 seconds of failures. */
export const MAX_CONSECUTIVE_FAILURES = 6;

/**
 * Longest a screen keeps polling. The auth-bff deletes a pending auth session 30 minutes after
 * it is created, so after that no status can ever come back.
 */
export const MAX_POLL_DURATION_MS = 30 * 60 * 1000;

/**
 * What one poll reports back.
 * - `continue`: the call worked; poll again.
 * - `failed`: the call failed; poll again, unless that was one failure too many.
 * - `stop`: the screen has what it needs (usually it has navigated); stop polling.
 */
export type PollResult = 'continue' | 'failed' | 'stop';

export interface StatusPollingOptions {
    intervalMs?: number;
    maxConsecutiveFailures?: number;
    maxDurationMs?: number;
}

/**
 * Polls `poll` every few seconds until the screen stops it or unmounts, so a stale tab can
 * never poll forever:
 * - A SessionExpiredError (the auth-bff's 404) stops polling straight away and sends the user
 *   to the host's session-expired screen.
 * - Too many failures in a row, or polling past the pending-session lifetime, does the same.
 *   The session is almost certainly gone by then, and restarting it is the way out.
 * - While the tab is hidden, no polls are sent. The next poll runs as soon as the tab is
 *   visible again.
 */
export default function useStatusPolling(
    poll: () => Promise<PollResult>,
    options: StatusPollingOptions = {},
) {
    const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
    const maxConsecutiveFailures = options.maxConsecutiveFailures ?? MAX_CONSECUTIVE_FAILURES;
    const maxDurationMs = options.maxDurationMs ?? MAX_POLL_DURATION_MS;

    let active = false;
    let unmounted = false;
    let startedAt = 0;
    let consecutiveFailures = 0;
    let pausedWhileHidden = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const isHidden = () => document.visibilityState === 'hidden';

    const clearTimer = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };

    const stop = () => {
        active = false;
        pausedWhileHidden = false;
        clearTimer();
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };

    const giveUp = (reason: string) => {
        console.warn(`[Auth MFE] Stopped polling: ${reason}.`);
        stop();
        redirectToSessionExpired();
    };

    const runPoll = async () => {
        timeoutId = null;
        if (!active) return;

        if (Date.now() - startedAt >= maxDurationMs) {
            giveUp(`still waiting after ${Math.round(maxDurationMs / 60_000)} minutes`);
            return;
        }

        if (isHidden()) {
            pausedWhileHidden = true;
            return;
        }

        let result: PollResult;
        try {
            result = await poll();
        } catch (error) {
            if (error instanceof SessionExpiredError) {
                stop();
                redirectToSessionExpired();
                return;
            }
            console.error('[Auth MFE] Status poll failed:', error);
            result = 'failed';
        }

        // The screen may have unmounted or stopped polling while the request was in flight.
        if (!active) return;

        if (result === 'stop') {
            stop();
            return;
        }

        consecutiveFailures = result === 'failed' ? consecutiveFailures + 1 : 0;
        if (consecutiveFailures >= maxConsecutiveFailures) {
            giveUp(`${consecutiveFailures} polls in a row failed`);
            return;
        }

        timeoutId = setTimeout(runPoll, intervalMs);
    };

    function onVisibilityChange() {
        if (!active || !pausedWhileHidden || isHidden()) return;
        pausedWhileHidden = false;
        runPoll();
    }

    /**
     * Polls once now, then every interval. Does nothing if polling is already running, or once
     * the screen has unmounted (a screen can call start() after an await that outlived it).
     */
    const start = () => {
        if (active || unmounted) return;
        active = true;
        startedAt = Date.now();
        consecutiveFailures = 0;
        document.addEventListener('visibilitychange', onVisibilityChange);
        runPoll();
    };

    onUnmounted(() => {
        unmounted = true;
        stop();
    });

    return { start, stop };
}
